'use client';

import { usePathname } from 'next/navigation';
import { ExtendedProductDetailProduct } from 'shared/types/product/Product';
import { ExtendedVariant } from 'shared/types/product/Variant';
import { getProductBuilder } from 'components/commercetools-ui/organisms/product/product-details/helpers/productBuilder';
import { RAILING_BUILDER_URL } from 'components/commercetools-ui/organisms/product/product-details/helpers/railingBuilder';
import { productNoun, variantDisplayNames, variantListHeading, variantPath } from 'helpers/seo/variant-seo';
import { externalLinkProps } from './external-link';
import { PRODUCT_OPTIONS_ID } from '../product/product-details/components/product-information';

interface VariantLinksProps {
  product: ExtendedProductDetailProduct;
  currentSku: string;
}

// Every variant a crawler would otherwise only find behind the configurator's dropdowns and
// buttons, as plain links, each carrying the variant's one display name (see variantDisplayNames).
// Visually the tile leads with what differs ("42 in Black") and repeats the shared product noun
// small underneath; both are inside the anchor, so the link text is still the full name. Capped so
// one row of four reads as a suggestion, not a sitemap (Joel: twelve looked like spam); the rest are
// a click away (see variantEscapeHatch) and listed in the ProductGroup structured data.
export const VARIANT_LINKS_MAX = 4;

const selectableAxes = (product: VariantLinksProps['product']) =>
  (product.variantDefiningAttributes ?? []).filter(
    (axis) => (axis.primitiveValues?.length ?? axis.values?.length ?? 0) > 1,
  );

/** Siblings that differ from the current variant on the fewest axes come first, so the capped list is the useful one. */
export const nearestFirst = <V extends { sku: string; attributes?: Record<string, unknown> }>(
  product: VariantLinksProps['product'],
  current: { attributes?: Record<string, unknown> } | undefined,
  siblings: V[],
): V[] => {
  const axes = selectableAxes(product);
  const distance = (v: { attributes?: Record<string, unknown> }) =>
    axes.filter((axis) => String(v.attributes?.[axis.key] ?? '') !== String(current?.attributes?.[axis.key] ?? '')).length;
  // A sibling identical on every axis (duplicate data) is not a useful neighbour; it sorts last.
  const rank = (d: number) => (d === 0 ? Number.MAX_SAFE_INTEGER : d);
  return siblings.map((v, i) => ({ v, i, d: rank(distance(v)) })).sort((a, b) => a.d - b.d || a.i - b.i).map((x) => x.v);
};

/**
 * "42 in Black Surface Mount End Cable Railing Post (Left Hand)" → ["42 in Black", "Surface Mount End Cable Railing Post (Left Hand)"].
 * Text is only split, never reordered, so the two lines rejoined with a space are the display name.
 */
export const splitDisplayName = (name: string, noun?: string): [string, string] => {
  if (!noun) return [name, ''];
  const at = name.indexOf(noun);
  if (at <= 0) return [name, ''];
  return [name.slice(0, at).trim(), name.slice(at).trim()];
};

/**
 * How a tile shows one display name on two lines without changing the anchor text. A composed name
 * splits at the noun (the lines rejoin with a space, as the name has). A name without the noun (a
 * merchandised variant name, "Express Black - Level End Post Package - 36\"") splits at its dash, and the dash
 * is kept in the anchor as screen-reader-only text, so the link text is still the exact name every
 * other surface uses — and the distinct name from variantDisplayNames is what gets split, so a
 * clash fallback is never bypassed.
 */
export const tileLines = (name: string, noun?: string): { lead: string; joiner: string; rest: string } => {
  const dash = name.indexOf(' - ');
  if (dash > 0 && !(noun && name.includes(noun))) return { lead: name.slice(0, dash), joiner: ' - ', rest: name.slice(dash + 3) };
  const [lead, rest] = splitDisplayName(name, noun);
  return { lead, joiner: ' ', rest };
};

// A product filed under any railing or handrail section, which the Railing Builder designs around.
const RAILING_SECTION_PREFIXES = ['/railing/', '/handrail/', '/cable-railing/', '/glass-railing/'];
const isRailingSystemProduct = (product: VariantLinksProps['product']) =>
  (product.categories ?? []).some((category) => RAILING_SECTION_PREFIXES.some((prefix) => `${category._url ?? ''}/`.startsWith(prefix)));

/**
 * Where to send someone who wants an option the four tiles don't show. A tread with 250 size/species/return
 * combinations, or a Signature post with 252 variants, has no listing a shopper can usefully scan, but each
 * has a builder that produces exactly that combination — so the escape hatch is the builder when the product
 * has one (the same resolvers the buy section uses), and this product's own configurator otherwise — never
 * the category listing, which shows other products rather than this one's options.
 */
export const variantEscapeHatch = (
  product: VariantLinksProps['product'],
  total: number,
  hidden: number,
): { label: string; href: string } | null => {
  // A product with a builder always links it, whatever the option count (Joel, 6 Oct 2026).
  const builder = getProductBuilder(product);
  if (builder) return { label: `Build your own in the ${builder.name}`, href: builder.href };
  // Everything else sold in a railing system — kits, handrail, rod, caps, hardware — is designed in the
  // Railing Builder (design.viewrail.com covers every railing system), even when the builder doesn't
  // produce this exact product. Samples and FLIGHT have no builder and keep the options link.
  if (isRailingSystemProduct(product)) return { label: 'Build your own in the Railing Builder', href: RAILING_BUILDER_URL };
  return hidden > 0 ? { label: `See all ${total} options`, href: `#${PRODUCT_OPTIONS_ID}` } : null;
};

const tileClass =
  'flex h-full flex-col justify-center gap-4 border border-neutral-black-200 px-16 py-12 transition-colors hover:border-primary-black focus-visible:border-primary-black focus-visible:outline-none';

const VariantLinks = ({ product, currentSku: routeSku }: VariantLinksProps) => {
  // Options switch the variant in place with history.replaceState, which Next mirrors into
  // usePathname, so the tiles follow the variant on screen rather than the one the page loaded with
  // (otherwise a tile links to the page being viewed and the original variant goes missing).
  const currentSku = usePathname()?.match(/\/p\/([^/]+)/)?.[1] ?? routeSku;
  const slug = product.slug;
  const current = product.variants?.find((variant) => variant.sku === currentSku);
  const siblings = nearestFirst<ExtendedVariant>(
    product,
    current,
    (product.variants ?? []).filter((variant) => variant.sku !== currentSku),
  );
  if (!slug || siblings.length === 0) return null;

  const noun = productNoun(product);
  const names = variantDisplayNames(product);
  const shown = siblings.slice(0, VARIANT_LINKS_MAX);
  const hidden = siblings.length - shown.length;
  const escape = variantEscapeHatch(product, product.variants?.length ?? siblings.length, hidden);

  return (
    <section className="w-full px-24 pt-64 font-gt text-primary-black md:px-38 md:pt-96" aria-labelledby="variant-links-heading">
      <div className="flex flex-wrap items-baseline justify-between gap-x-24 gap-y-8">
        <h2 id="variant-links-heading" className="text-28 font-medium leading-[1.2] md:text-40">
          {variantListHeading(noun)}
        </h2>
        {escape && (
          <a href={escape.href} {...externalLinkProps(escape.href)} className="text-14 font-medium underline underline-offset-4 hover:opacity-70">
            {escape.label}
          </a>
        )}
      </div>
      <ul className="mt-24 grid grid-cols-2 gap-8 md:mt-32 md:grid-cols-3 lg:grid-cols-4">
        {shown.map((variant) => {
          const name = names.get(variant.sku) ?? variant.name ?? variant.sku;
          const { lead, joiner, rest } = tileLines(name, noun);
          return (
            <li key={variant.sku}>
              <a href={variantPath(slug, variant.sku)} className={tileClass}>
                <span className="text-16 font-medium leading-[1.2]">{lead}</span>
                {rest && (
                  <>
                    {joiner === ' ' ? ' ' : <span className="sr-only">{joiner}</span>}
                    <span className="text-13 leading-[1.3] text-[#5e5e5e]">{rest}</span>
                  </>
                )}
              </a>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default VariantLinks;
